import React, { useEffect, useState } from 'react';
import { AlertTriangle, Copy, KeyRound, Loader2, LockKeyhole, Server, User } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';

import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import type { SshProfile } from '../../../types/ssh';
import { createEmptySshProfile, normalizeSshProfile } from '../../../lib/sshProfiles';
import { generateSshKeyPair } from '../../../lib/sshKeygen';

interface SshProfileFormProps {
  profile?: SshProfile | null;
  onCancel: () => void;
  onSave: (profile: SshProfile) => void;
}

export function SshProfileForm({ profile, onCancel, onSave }: SshProfileFormProps) {
  const [draft, setDraft] = useState<SshProfile>(profile || createEmptySshProfile());
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    setDraft(profile || createEmptySshProfile());
    setStatusMessage(null);
    setIsGeneratingKey(false);
  }, [profile]);

  const handleSave = () => {
    const normalized = normalizeSshProfile({
      ...draft,
      port: Number(draft.port) || 22,
    });

    if (!normalized) {
      setStatusMessage('Profile name, host, and username are required.');
      return;
    }

    onSave(normalized);
  };

  const handleGenerateKeyPair = async () => {
    setIsGeneratingKey(true);
    setStatusMessage(null);

    try {
      const comment = [draft.username || 'user', draft.host || 'host'].join('@').replace('@host', '').trim() || draft.name || 'mapleos-generated';
      const generated = await generateSshKeyPair({
        comment,
        nameHint: draft.name || draft.host || draft.username || 'mapleos',
      });

      setDraft((current) => ({
        ...current,
        authType: 'privateKey',
        privateKey: generated.privateKeyPem,
        publicKey: generated.publicKeyOpenSsh,
        privateKeyName: generated.keyName,
      }));
      setStatusMessage('New SSH keypair generated. Save the profile to keep using this private key.');
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : 'SSH keypair generation failed.');
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const handleCopy = async (value: string, label: string) => {
    if (!value.trim()) {
      setStatusMessage(`No ${label.toLowerCase()} available to copy.`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setStatusMessage(`${label} copied to clipboard.`);
    } catch {
      setStatusMessage(`Failed to copy ${label.toLowerCase()} to clipboard.`);
    }
  };

  return (
    <Card className="border-border/60 bg-black/30 shadow-none">
      <CardHeader className="space-y-1">
        <CardTitle className="text-sm font-semibold text-white">
          {profile ? 'Edit SSH profile' : 'Add SSH profile'}
        </CardTitle>
        <CardDescription className="text-xs text-gray-400">
          Save host metadata for quick reuse from the MapleOS terminal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="ssh-profile-name" className="text-xs uppercase tracking-wide text-gray-300">Profile name</Label>
            <div className="relative">
              <Server className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                id="ssh-profile-name"
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                placeholder="Production web server"
                className="border-border/60 bg-black/40 pl-9 text-white"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ssh-profile-host" className="text-xs uppercase tracking-wide text-gray-300">Host / IP</Label>
            <div className="relative">
              <Server className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                id="ssh-profile-host"
                value={draft.host}
                onChange={(event) => setDraft((current) => ({ ...current, host: event.target.value }))}
                placeholder="192.168.1.10"
                className="border-border/60 bg-black/40 pl-9 text-white"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ssh-profile-port" className="text-xs uppercase tracking-wide text-gray-300">Port</Label>
            <Input
              id="ssh-profile-port"
              type="number"
              min={1}
              value={draft.port}
              onChange={(event) => setDraft((current) => ({ ...current, port: Number(event.target.value) || 22 }))}
              className="border-border/60 bg-black/40 text-white"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="ssh-profile-username" className="text-xs uppercase tracking-wide text-gray-300">Username</Label>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                id="ssh-profile-username"
                value={draft.username}
                onChange={(event) => setDraft((current) => ({ ...current, username: event.target.value }))}
                placeholder="ubuntu"
                className="border-border/60 bg-black/40 pl-9 text-white"
              />
            </div>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-gray-300">Authentication</Label>
            <Select
              value={draft.authType}
              onValueChange={(value: 'password' | 'privateKey') => setDraft((current) => ({ ...current, authType: value }))}
            >
              <SelectTrigger className="border-border/60 bg-black/40 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="password">
                  <span className="inline-flex items-center gap-2">
                    <LockKeyhole className="h-4 w-4" />
                    Password at connect time
                  </span>
                </SelectItem>
                <SelectItem value="privateKey">
                  <span className="inline-flex items-center gap-2">
                    <KeyRound className="h-4 w-4" />
                    Saved private key
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2 flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => void handleGenerateKeyPair()}
              disabled={isGeneratingKey}
              className="bg-cyan-600 text-white hover:bg-cyan-500"
            >
              {isGeneratingKey ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
              {draft.privateKey ? 'Regenerate keypair' : 'Generate keypair'}
            </Button>
            {draft.publicKey && (
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleCopy(draft.publicKey || '', 'Public key')}
                className="border-border/60 bg-transparent text-white hover:bg-white/10"
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy public key
              </Button>
            )}
            {draft.privateKey && (
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleCopy(draft.privateKey || '', 'Private key')}
                className="border-border/60 bg-transparent text-white hover:bg-white/10"
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy private key
              </Button>
            )}
          </div>

          {draft.authType === 'privateKey' && (
            <>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ssh-profile-key-name" className="text-xs uppercase tracking-wide text-gray-300">Private key label</Label>
                <Input
                  id="ssh-profile-key-name"
                  value={draft.privateKeyName || ''}
                  onChange={(event) => setDraft((current) => ({ ...current, privateKeyName: event.target.value }))}
                  placeholder="id_rsa_mapleos"
                  className="border-border/60 bg-black/40 text-white"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ssh-profile-public-key" className="text-xs uppercase tracking-wide text-gray-300">Public key (copy this to authorized_keys)</Label>
                <Textarea
                  id="ssh-profile-public-key"
                  value={draft.publicKey || ''}
                  onChange={(event) => setDraft((current) => ({ ...current, publicKey: event.target.value }))}
                  placeholder="ssh-rsa AAAAB3..."
                  className="min-h-24 border-border/60 bg-black/40 font-mono text-xs text-white"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ssh-profile-private-key" className="text-xs uppercase tracking-wide text-gray-300">Private key contents</Label>
                <Textarea
                  id="ssh-profile-private-key"
                  value={draft.privateKey || ''}
                  onChange={(event) => setDraft((current) => ({ ...current, privateKey: event.target.value }))}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  className="min-h-36 border-border/60 bg-black/40 font-mono text-xs text-white"
                />
              </div>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    MapleOS stores saved private keys in terminal settings for now. They are reusable by Terminal and DeMythos, but they are not encrypted at rest in v1. Treat this device and account as sensitive.
                  </p>
                </div>
              </div>
            </>
          )}

          {statusMessage && (
            <div className="md:col-span-2 rounded-md border border-border/60 bg-black/20 px-3 py-2 text-xs text-gray-300">
              {statusMessage}
            </div>
          )}

          <div className="flex gap-2 md:col-span-2">
            <Button variant="outline" onClick={onCancel} className="flex-1 border-border/60 bg-transparent text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleSave} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-500">
              Save profile
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
